// Lightweight markdown → HTML renderer used by the chat surface.
//
// Ported from the previous static frontend (frontend/app.js) to keep the
// citation-chip semantics intact. The LLM emits inline source-document tags
// in square brackets, e.g. `[Outpost CIM]`, `[Asset Book Q2 2026]`,
// `[MIP Grant: Trent Cameron Top-up 2025-12-23]`. For each unique tag we
// render a numbered chip that links to the matching source-panel entry by
// index. Unresolved tags render as a small inline label so the LP still
// sees the source name.

export type CitationMap = {
  // Lower-cased document_name → source-panel index (1-based).
  byName: Map<string, number>;
};

export function buildCitationMap(
  sources: { document_name: string; index: number }[] | undefined,
): CitationMap {
  const byName = new Map<string, number>();
  if (Array.isArray(sources)) {
    for (const s of sources) {
      if (s?.document_name) byName.set(s.document_name.toLowerCase(), s.index);
    }
  }
  return { byName };
}

// Convert citation tags into placeholder tokens before HTML escaping.
function injectCitationPlaceholders(text: string, map: CitationMap): string {
  return text.replace(/\[([A-Z][^\[\]\n]{1,120})\]/g, (match, inner: string) => {
    if (/^Sources?\s+\d/i.test(inner)) return match;
    const parts = inner.split(/\s*[;|]\s*|\s*,\s*(?=[A-Z])/);
    const tokens: string[] = [];
    for (const raw of parts) {
      const tag = raw.trim();
      if (!tag) continue;
      const idx = map.byName.get(tag.toLowerCase());
      if (idx !== undefined) {
        tokens.push(`%%CITE_${idx}%%`);
      } else {
        tokens.push(`%%CITE_UNRESOLVED:${tag}%%`);
      }
    }
    return tokens.length > 0 ? tokens.join("") : match;
  });
}

export function renderMarkdown(text: string, map: CitationMap): string {
  let html = injectCitationPlaceholders(text, map);

  // HTML escape (citation placeholders are safe — they're ASCII tokens).
  html = html
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Headers
  html = html
    .replace(/^### (.+)$/gm, "<h3>$1</h3>")
    .replace(/^## (.+)$/gm, "<h2>$1</h2>")
    .replace(/^# (.+)$/gm, "<h1>$1</h1>");

  // Bold + italic (***bold-italic*** before **bold** before *italic*)
  html = html
    .replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>");

  // Inline code
  html = html.replace(/`([^`]+)`/g, "<code>$1</code>");

  // Tables (markdown pipe tables). We collect rows and let a later pass
  // wrap them in <table>; the separator row is dropped via comment marker.
  html = html.replace(/^\|(.+)\|$/gm, (match) => {
    const cells = match.split("|").filter((c) => c.trim());
    if (cells.every((c) => /^[\s\-:]+$/.test(c))) return "<!-- separator -->";
    return "<tr>" + cells.map((c) => `<td>${c.trim()}</td>`).join("") + "</tr>";
  });

  // Wrap consecutive <tr>s into a <table>, promote the first row to <th>s.
  // The run must also span the `<!-- separator -->` marker that sits between
  // the header row and the body rows; otherwise the marker splits the run in
  // two and the first BODY row gets wrongly promoted to <th> (the table-header
  // bug). Including the marker keeps header + separator + body as one match so
  // only the true header row (rows[0], after the separator is stripped below)
  // becomes <th>. Blank lines between separate tables still break the run.
  html = html.replace(/((?:(?:<tr>.*<\/tr>|<!-- separator -->)\n?)+)/g, (match) => {
    const cleaned = match.replace(/<!-- separator -->\n?/g, "");
    if (!cleaned.trim()) return "";
    const rows = cleaned.trim().split("\n").filter((r) => r.includes("<tr>"));
    if (rows.length > 0) {
      rows[0] = rows[0].replace(/<td>/g, "<th>").replace(/<\/td>/g, "</th>");
    }
    // Wrap in a horizontally-scrollable container. Wide tables (e.g. an
    // 8-column per-asset breakdown) otherwise crush their columns to fit the
    // chat bubble and wrap each cell one word per line. The wrapper scrolls;
    // CSS (.md-table-wrap) caps cell width so prose wraps readably.
    return '<div class="md-table-wrap"><table>' + rows.join("\n") + "</table></div>";
  });

  // Lists
  html = html.replace(/^- (.+)$/gm, "<li>$1</li>");
  html = html.replace(/((?:<li>.*<\/li>\n?)+)/g, "<ul>$1</ul>");
  html = html.replace(/^\d+\. (.+)$/gm, "<li>$1</li>");

  // Paragraphs
  html = html.replace(/\n\n/g, "</p><p>");
  html = "<p>" + html + "</p>";
  // Strip <p> wrappers around block elements. `div` covers the .md-table-wrap
  // container emitted above (markdown.ts emits no other <div>), so the table
  // wrapper is not left orphaned inside an invalid <p>…</p>.
  html = html.replace(/<p>(<(?:h[123]|ul|ol|table|div)\b)/g, "$1");
  html = html.replace(/(<\/(?:h[123]|ul|ol|table|div)>)<\/p>/g, "$1");
  html = html.replace(/<p><\/p>/g, "");

  // Replace placeholders → real chips. We emit a button so it's keyboard
  // accessible and screen-reader friendly. The click handler is attached
  // by the React layer via event delegation on a wrapping element.
  html = html.replace(
    /%%CITE_(\d+)%%/g,
    (_m, idx: string) =>
      `<button type="button" class="citation" data-source="${idx}" aria-label="Show source ${idx}">${idx}</button>`,
  );
  // Unresolved tags — the source panel didn't include a row for this name.
  // Either the doc was suppressed by the worker's anti-fabrication guard
  // (gated doc the model invoked but that wasn't in the retrieval set), or
  // the model invented a tag that doesn't match any document. In both cases
  // rendering the tag text (or even a tooltip) leaks the model's claim
  // about a source the LP cannot inspect. Strip it from the rendered HTML;
  // surrounding prose still carries the substantive content.
  html = html.replace(/%%CITE_UNRESOLVED:[^%]+%%/g, "");
  // Tidy doubled whitespace where a tag was stripped mid-sentence.
  html = html.replace(/\s{2,}/g, " ").replace(/\s+([.,;:!?])/g, "$1");

  return html;
}
